import type { Me, Tenant } from '@bk/contracts';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { api } from './api';

export interface TenantContextValue {
  tenant: Tenant;
  me: Me;
  isManager: boolean;
  /** Aree della pagina Procedure su cui l'utente lavora: tutte (titolari e amministratori) o quelle di cui è responsabile. */
  procedureAreas: 'tutte' | string[];
  /** Percorso API del tenant: `t('/people')` → `/tenants/<id>/people`. */
  t: (path: string) => string;
  /** Il livello su cui si lavora (ADR 0210 § 8): la sede scelta, o null per tutta l'azienda. */
  livello: string | null;
  setLivello: (siteId: string | null) => void;
}

export const TenantContext = createContext<TenantContextValue | null>(null);

export function useTenant(): TenantContextValue {
  const value = useContext(TenantContext);
  if (!value) throw new Error('useTenant fuori da TenantContext');
  return value;
}

export function useTenantQuery<T>(key: unknown[], path: string, options: { enabled?: boolean; refetchInterval?: number | false | ((data: T | undefined) => number | false); refetchIntervalInBackground?: boolean } = {}) {
  const { tenant, t } = useTenant();
  const { refetchInterval, ...rest } = options;
  return useQuery({
    queryKey: ['tenant', tenant.id, ...key],
    queryFn: () => api<T>(t(path)),
    ...rest,
    ...(refetchInterval !== undefined ? { refetchInterval: typeof refetchInterval === 'function' ? (q: { state: { data?: T } }) => refetchInterval(q.state.data) : refetchInterval } : {}),
  });
}
